import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLibraryBrowse, useLibrarySections, useMe, keys } from '@/lib/queries.ts';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.ts';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { Modal } from '@/ui/Modal.tsx';
import { SORTS, defaultDesc, loadSort, saveSort, sortName, type LibrarySort } from '@/lib/sort.ts';
import { Poster } from './Poster.tsx';
import { useItemMenu } from '@/stores/itemMenu.ts';
import { usePlayItem } from './usePlayItem.ts';

/**
 * One library, as a grid.
 *
 * A grid rather than rows, unlike the home screen: rows are for curation —
 * "the things we think you want" — and a library is a list you came to walk.
 *
 * IT LOADS AS YOU GO. The first version asked Plex for one page and stopped,
 * which was not a deliberate cap — sixty is simply what Plex returns when you
 * do not say otherwise. On a five thousand film library that meant 4,940
 * films the app behaved as though it did not have.
 *
 * No heading: the LibraryBar in the shell above already names the library, in
 * the same words and marked as current.
 */
export function SectionPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { data: sections } = useLibrarySections();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const { open } = usePlayItem();
  const [menu, setMenu] = useState(false);

  const section = sections?.find((s) => s.key === key);

  /*
   * Resolved from the section, so the admin's default applies to anybody who
   * has not chosen — and re-resolved when the sections arrive, because on a
   * cold load the grid renders before that request lands.
   */
  const [order, setOrder] = useState(() => loadSort(key ?? ''));
  useEffect(() => {
    if (!key) return;
    setOrder(loadSort(key, section
      ? { sort: section.defaultSort, desc: section.defaultDesc }
      : undefined));
  }, [key, section?.defaultSort, section?.defaultDesc]);

  const {
    data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useLibraryBrowse(key, order.sort, order.desc);

  const title = section?.title ?? 'Library';
  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const total = data?.pages[0]?.total;

  // BACK returns to the library home rather than leaving the app, which is
  // the reflex, and the thing a screen without it gets wrong.
  useEffect(() => pushKeyLayer((k: NavKey) => {
    if (k === 'back') { navigate('/library'); return true; }
    // MENU is where options live everywhere else in this app; a sort control
    // permanently on screen would be a focus stop above four thousand posters
    // for a setting changed about once.
    if (k === 'menu') { setMenu(true); return true; }
    return false;
  }), [navigate]);

  /*
   * MORE ARRIVES BEFORE YOU REACH THE END.
   *
   * A sentinel near the bottom rather than a "load more" button: on a D-pad
   * you are already holding the button that should mean "more", and a button
   * that steals focus at the end of every page turns one gesture into two.
   *
   * rootMargin is deliberately large. The observer has to fire while the next
   * page is still off screen, or the grid stops dead under the cursor while
   * Plex answers — which reads as the app freezing rather than loading.
   */
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting && !isFetchingNextPage) void fetchNextPage(); },
      { rootMargin: '800px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, items.length]);

  function choose(sort: LibrarySort) {
    const desc = defaultDesc(sort);
    setOrder({ sort, desc });
    saveSort(key ?? '', sort, desc);
    setMenu(false);
  }

  /*
   * Offered only to an admin, and only when this is not already the default —
   * a row that does nothing is worse than no row, especially as a focus stop
   * on a television.
   */
  const isDefault = section?.defaultSort === order.sort && !!section?.defaultDesc === order.desc;
  const canSetDefault = !!me?.user.can.admin && !!key && !isDefault;

  async function makeDefault() {
    if (!key) return;
    await api.setSectionSort(key, order.sort, order.desc);
    // The section list carries the default, so it is what has gone stale.
    await qc.invalidateQueries({ queryKey: keys.librarySections });
    setMenu(false);
  }

  return (
    <>
      {isLoading && <div className="muted center">Loading {title}…</div>}
      {error && <div className="muted center">Could not load {title}.</div>}

      <div className="libe-grid">
        {items.map((item, i) => (
          <Poster
            key={item.id}
            item={item}
            onSelect={open}
            onOptions={(it) => useItemMenu.getState().open(it.id)}
            autoFocus={i === 0}
          />
        ))}
      </div>

      {/* Sits after the grid, so intersecting it means the cursor is near the
          end of what has been loaded. */}
      <div ref={sentinel} />

      {hasNextPage && (
        <div className="muted center libe-more">
          {isFetchingNextPage
            ? 'Loading more…'
            : `${items.length}${total ? ` of ${total}` : ''} — keep going`}
        </div>
      )}
      {!isLoading && !error && items.length === 0 && (
        <div className="muted center">Nothing in here.</div>
      )}

      {menu && (
        <Modal className="account-dialog" onClose={() => setMenu(false)} labelledBy="sort-title">
          <div className="menu-head">
            <div>
              <div className="big" id="sort-title">{title}</div>
              <div className="muted">
                Sorted by {sortName(order.sort).toLowerCase()}
                {isDefault ? ' (the default here)' : ''}
                {total ? ` · ${total} items` : ''}
              </div>
            </div>
          </div>
          <div className="menu-items">
            {canSetDefault && (
              <Focusable
                id="sort-default"
                className="btn menu-item"
                onSelect={() => { void makeDefault(); }}
              >
                Use {sortName(order.sort).toLowerCase()} for everyone
              </Focusable>
            )}

            {SORTS.map((s) => (
              <Focusable
                key={s.id}
                id={`sort-${s.id}`}
                className={`btn menu-item${s.id === order.sort ? ' menu-item-on' : ''}`}
                onSelect={() => choose(s.id)}
              >
                {s.name}
                {s.id === order.sort && <span className="menu-value">current</span>}
              </Focusable>
            ))}
            {/* Last: BACK closes this too, so the top of the list belongs to
                the thing somebody opened it for. */}
            <Focusable id="sort-close" className="btn menu-item" onSelect={() => setMenu(false)}>
              Close
            </Focusable>
          </div>
        </Modal>
      )}
    </>
  );
}
